import { debounce } from "lodash"
import { useCallback, useEffect, useRef, useState } from "react"
import { FaArrowRight, FaAt, FaChevronDown, FaEuroSign, FaFile, FaGlobe, FaLock, FaPhone, FaPlus, FaTimes } from "react-icons/fa"
import { useTranslations } from "../../layers/Localization"
import { Link } from "react-router-dom"
import { useFormSubmissions } from "./Form"
import { useApi } from '../../hooks/useApi'
import { Loading } from "../../components/layout/Popup"

function Label({ children, label, help, disabled, error, showError, ...props }) {
    return <label {...props} className={`flex flex-col ${disabled ? 'opacity-70' : ''} ${props.className || ''}`}>
        {label && <div className="mb-2 font-medium">{label}</div>}
        {children}
        <div className={`ml-2 text-sm text-red-500 transition-all overflow-hidden ${showError ? 'max-h-20 pt-2' : 'max-h-0 pt-0'}`}>{error}</div>
        {help && <div className={`ml-2 text-xs text-gray-400 pt-2`}>{help}</div>}
    </label>
}

function useErrors(value, invalid = null, focus, blur) {
    const ref = useRef()
    const [error, setError] = useState(invalid)
    const [isFocused, setIsFocused] = useState(false)
    const [hasBlurred, setHasBlurred] = useState(false)
    const formSubmissions = useFormSubmissions()
    const couldShowError = !isFocused && (hasBlurred || formSubmissions > 0)
    const showError = error && couldShowError

    useEffect(() => {
        if (ref.current) {
            ref.current.setCustomValidity(invalid || '')
            setError(ref.current.validationMessage || '')
        }
    }, [ref.current, invalid])
    useEffect(() => {
        if (ref.current) setError(ref.current.validationMessage || '')
    }, [ref.current, isFocused, formSubmissions, value])

    const onFocus = e => {
        setIsFocused(true)
        focus?.(e)
    }
    const onBlur = e => {
        setIsFocused(false)
        setHasBlurred(true)
        blur?.(e)
    }
    const className = showError ? 'border-red-500' : ''

    return [{ ref, onFocus, onBlur, className }, { error, showError }]
}

export function TextInput({ value, onChange, label, invalid, help, icon, onIconClick, className, ...props }) {
    const [inputProps, labelProps] = useErrors(value, invalid)
    return <Label label={label} className={className} help={help} disabled={props.disabled} {...labelProps}>
        <div className={`w-full flex rounded-lg border border-gray-300 bg-gray-200 ${inputProps.className}`}>
            <input {...props} {...inputProps} value={value ?? ""} onChange={e => onChange(e.target.value)} className={`px-3 py-2 grow min-w-0 bg-gray-200 focus:outline-none focus:ring focus:ring-grey-300 focus:z-10 placeholder:text-gray-500 ${icon ? 'rounded-l-lg' : 'rounded-lg'}`} />
            {icon && <div
                className={`p-3 rounded-r-lg border-l border-gray-300 flex items-center text-gray-800 ${onIconClick ? 'cursor-pointer' : ''}`}
                onClick={onIconClick}
            >
                {icon}
            </div>}
        </div>
    </Label>
}

export function NumberInput({ value, onChange, shownDecimals, ...props }) {
    const [showDecimals, setShowDecimals] = useState(!!shownDecimals)
    const onChangeNumber = v => {
        onChange(v && !isNaN(Number(v)) ? Number(v) : null)
        setShowDecimals(false)
    }
    const shownValue = showDecimals && typeof value === 'number' && !isNaN(value) && (value * Math.pow(10, shownDecimals) % 1) === 0 ? value.toFixed(shownDecimals) : value
    return <TextInput min="0" step="1" {...props} value={shownValue} type="number" onChange={onChangeNumber} />
}

export function MoneyInput(props) {
    return <NumberInput min="0" step="0.01" shownDecimals={2} icon={<FaEuroSign />} {...props} />
}

export function PhoneInput(props) {
    return <TextInput {...props} type="tel" icon={<FaPhone />} pattern="^\+?[0-9]{7,15}$" />
}

export function EmailInput(props) {
    return <TextInput {...props} type="email" icon={<FaAt />} />
}

export function PasswordInput(props) {
    return <TextInput {...props} type="password" icon={<FaLock />} />
}

export function UrlInput(props) {
    return <TextInput {...props} type="url" icon={<FaGlobe />} />
}

export function TextareaInput({ value, onChange, label, invalid, help, className, ...props }) {
    const [height, setHeight] = useState("5px")
    const [inputProps, labelProps] = useErrors(value, invalid)

    useEffect(() => {
        if (inputProps.ref.current && height !== '5px') setHeight("5px")
    }, [value])
    useEffect(() => {
        if (inputProps.ref.current && height === '5px') setHeight(inputProps.ref.current.scrollHeight + "px")
    }, [height])

    return <Label label={label} className={className} help={help} disabled={props.disabled} {...labelProps}>
        <textarea {...props} {...inputProps} value={value ?? ""} onChange={e => onChange(e.target.value)} style={{ height }} className={`w-full rounded-lg px-3 py-2 grow min-w-0 min-h-[80px] overflow-hidden bg-gray-200 border border-gray-300 focus:outline-none focus:ring focus:ring-grey-300 focus:z-10 placeholder:text-gray-500 ${inputProps.className}`} />
    </Label>
}

export function DateInput({ value, onChange, label, invalid, help, ...props }) {
    return <DateTimeInput {...{ ...props, value, onChange, label, invalid, help }} dateOnly />
}

export const toDateOnly = (date = new Date()) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0))

export function DateTimeInput({ value, onChange, label, invalid, help, dateOnly, className, ...props }) {
    const [inputProps, labelProps] = useErrors(value, invalid)

    const twoChar = nb => nb > 9 ? nb : `0${nb}`
    const dateToStringDate = date => dateOnly
        ? !date ? '' : `${twoChar(date.getUTCFullYear())}-${twoChar(date.getUTCMonth() + 1)}-${twoChar(date.getUTCDate())}`
        : !date ? '' : `${twoChar(date.getFullYear())}-${twoChar(date.getMonth() + 1)}-${twoChar(date.getDate())}`
    const dateToStringTime = date => !date ? '' : `${twoChar(date.getHours())}:${twoChar(date.getMinutes())}`

    const [stringDate, setStringDate] = useState(dateToStringDate(value))
    const [stringTime, setStringTime] = useState(dateToStringTime(value))

    useEffect(() => {
        if (value instanceof Date && value.getTime() !== toDateOnly(value).getTime()) {
            value = toDateOnly(value)
            onChange(toDateOnly(value))
        }
        if (dateToStringDate(value) !== stringDate || dateToStringTime(value) !== stringTime) {
            setStringDate(dateToStringDate(value))
            setStringTime(dateToStringTime(value))
        }
    }, [value])

    const changeValue = (stringDate, stringTime) => {
        if (dateToStringDate(value) !== stringDate || dateToStringTime(value) !== stringTime) {
            const isStringDateComplete = stringDate && Number(stringDate.split('-')[0]) >= 1900
            if (isStringDateComplete) {
                const [years, months, days] = stringDate.split('-').map(Number)
                const [hours, minutes] = stringTime ? stringTime.split(':').map(Number) : dateToStringTime(new Date()).split(':').map(Number)
                if (!value || dateToStringDate(value) !== stringDate || dateToStringTime(value) !== stringTime) {
                    const newDate = dateOnly
                        ? toDateOnly(new Date(years, months - 1, days))
                        : new Date(years, months - 1, days, hours, minutes)
                    onChange(newDate)
                }
            } else if (value) {
                onChange(null)
            }
        }
    }

    const changeStringDate = value => {
        setStringDate(value)
        changeValue(value, stringTime)
    }
    const changeStringTime = value => {
        setStringTime(value)
        changeValue(stringDate, value)
    }

    return <Label label={label} className={className} help={help} disabled={props.disabled} {...labelProps}>
        {dateOnly
            ? <div className="w-full flex">
                <input {...props} {...inputProps} type="date" value={stringDate} onChange={e => changeStringDate(e.target.value)} className={`grow px-3 py-2 h-[42px] bg-gray-200 border border-gray-300 rounded-lg ${props.disabled ? '' : 'cursor-pointer'} focus:outline-none focus:ring focus:ring-blue-300 focus:z-10 ${inputProps.className}`} />
            </div>
            : <div className="w-full flex rounded-lg">
                <input {...props} {...inputProps} type="date" value={stringDate} onChange={e => changeStringDate(e.target.value)} className={`grow px-3 py-2 h-[42px] bg-gray-200 border border-gray-300 border-r-0 rounded-l-lg ${props.disabled ? '' : 'cursor-pointer'} focus:outline-none focus:ring focus:ring-blue-300 focus:z-10 ${inputProps.className}`} />
                <input {...props} {...inputProps} type="time" value={stringTime} onChange={e => changeStringTime(e.target.value)} className={`grow px-3 py-2 h-[42px] bg-gray-200 border border-gray-300 border-l-0 rounded-r-lg ${props.disabled ? '' : 'cursor-pointer'} focus:outline-none focus:ring focus:ring-blue-300 focus:z-10 ${inputProps.className}`} />
            </div>
        }
    </Label>
}

export function TimeInput({ value, onChange, label, invalid, help, className, ...props }) {
    const [inputProps, labelProps] = useErrors(value, invalid)

    return <Label label={label} className={className} help={help} disabled={props.disabled} {...labelProps}>
        <div className="w-full flex">
            <input {...props} {...inputProps} type="time" value={value ?? ""} onChange={e => onChange(e.target.value || null)} className={`grow px-3 py-2 bg-gray-200 border border-gray-300 rounded-lg ${props.disabled ? '' : 'cursor-pointer'} focus:outline-none focus:ring focus:ring-blue-300 focus:z-10 ${inputProps.className}`} />
        </div>
    </Label>
}

export function Checkbox({ value, onChange, label, invalid, help, center, className, ...props }) {
    const [inputProps, labelProps] = useErrors(value, invalid)

    return <div className={`${className || ''} ${center ? 'self-stretch mt-6 flex items-center justify-center' : ''}`}>
        <div className={`flex flex-col gap-0.5 ${props.disabled ? 'opacity-70' : ''}`}>
            <label className="flex items-center gap-3 cursor-pointer">
                <input {...props} {...inputProps} checked={!!value} onChange={() => onChange(!value)} type="checkbox"
                    className={`appearance-none bg-white m-0 w-4 h-4 border border-gray-400 rounded cursor-pointer grid place-content-center shrink-0
                            before:w-2 before:h-2 before:flex before:justify-center before:items-center before:font-bold 
                            checked:bg-blue-500 checked:border-blue-500 before:content-none checked:before:content-['✓'] before:text-white
                            focus:outline-none focus:ring focus:ring-blue-300`}
                />
                <div>{label}</div>
            </label>
            <div className={`text-sm text-red-500 transition-all overflow-hidden ${labelProps.showError ? 'max-h-20 pt-2' : 'max-h-0 pt-0'}`}>{labelProps.error}</div>
            {help && <div className="text-gray-400 text-xs">{help}</div>}
        </div>
    </div>
}

export function Select({ value, onChange, label, invalid, help, options = [], required, className, ...props }) {
    const [inputProps, labelProps] = useErrors(value, invalid)

    const onSelectChange = e => {
        if (options.some(x => x.value === e.target.value)) onChange(e.target.value)
        else if (!required && !e.target.value) onChange(null)
        else if (options.some(x => String(x.value) === e.target.value)) onChange(options.find(x => String(x.value) === e.target.value).value)
    }

    return <Label label={label} className={className} help={help} disabled={props.disabled} {...labelProps}>
        <div className="relative w-full min-w-[272px] flex">
            <div className="absolute right-4 top-0 bottom-0 flex items-center pointer-events-none">
                <FaChevronDown className="flex items-center justify-center z-20" />
            </div>
            <select {...props} {...inputProps} required={required} value={value ?? ""} onChange={onSelectChange} className={`pl-3 pr-[30px] py-2 overflow-hidden grow min-w-0 bg-gray-200 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-grey-300 focus:z-10 appearance-none cursor-pointer ${inputProps.className}`}>
                <option value={null} className={`bg-white ${required ? 'hidden' : ''}`}></option>
                {options.map(x =>
                    <option key={x.value} value={x.value} className="bg-white">{x.label}</option>
                )}
            </select>
        </div>
    </Label>
}

export function AsyncSelect({ value, onChange, label, required, invalid, help, options = async search => ([]), onItemLoad, add, className, ...props }) {
    const [search, setSearch] = useState(value ?? "")
    const [currentOption, setCurrentOption] = useState(null)
    const [loadedOptions, setLoadedOptions] = useState([])
    const [isFocused, setIsFocused] = useState(false)
    const t = useTranslations()

    const fullOptions = [
        currentOption,
        ...loadedOptions.filter(x => x.value !== currentOption?.value)
    ].filter(x => x)

    const load = useCallback(async search => options(search).then(setLoadedOptions), [options])
    const loadDebounced = useCallback(debounce(search => {
        options(search).then(setLoadedOptions)
    }, 200, { leading: false, trailing: true }), [options])

    useEffect(() => {
        if (value && currentOption?.value !== value) options(value).then(options => setCurrentOption(options[0]))
        if (!value) setCurrentOption(null)
    }, [value, options])
    useEffect(() => {
        setSearch(currentOption?.label || '')
        onItemLoad?.(currentOption?.item)
    }, [currentOption])

    useEffect(() => {
        if (loadedOptions.length) load(value ? '' : search)
    }, [load])

    useEffect(() => {
        if (!isFocused && !value) setSearch('')
        if (isFocused && !loadedOptions.length) load(value ? '' : search)
    }, [isFocused])

    const onSearch = search => {
        if (value) {
            onChange(null, null)
            setSearch('')
            loadDebounced('')
            setIsFocused(true)
        } else {
            setSearch(search)
            loadDebounced(value ? '' : search)
        }
    }
    const selectOption = option => {
        setIsFocused(false)
        onChange(option.value, option.item)
        setCurrentOption(option)
        setLoadedOptions([])
    }

    const focus = () => {
        setIsFocused(true)
    }
    const blur = () => {
        setTimeout(() => setIsFocused(false), 300)
    }
    const keyDown = e => {
        if (!['ArrowDown', 'ArrowUp'].includes(e.key) || !loadedOptions.length || !isFocused) return
        e.preventDefault()
        const nextIndex = loadedOptions.findIndex(x => x.value === value) + (e.key === 'ArrowDown' ? 1 : -1)
        const actualNextIndex = nextIndex < 0 ? loadedOptions.length - 1 : nextIndex > loadedOptions.length - 1 ? 0 : nextIndex
        onChange(loadedOptions[actualNextIndex].value, loadedOptions[actualNextIndex].item)
    }

    const [inputProps, labelProps] = useErrors(value, invalid, focus, blur)

    return <Label label={label} className={className} help={help} disabled={props.disabled} error={labelProps.error} showError={labelProps.showError && !isFocused}>
        <div className="relative w-full">
            <input {...props} {...inputProps} required={value ? undefined : required} value={search ?? ""} onChange={e => onSearch(e.target.value)} onKeyDown={keyDown} className={`pl-3 pr-[30px] py-2 overflow-hidden w-full min-w-0 bg-gray-200 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-grey-300 focus:z-10 ${currentOption?.link ? 'pr-[68px]' : 'pr-[34px]'} ${!isFocused ? inputProps.className : ''}`} />
            <div className={`absolute z-20 top-0 bottom-0 right-4 flex items-center gap-3 ${props.disabled ? 'opacity-70' : ''}`}>
                {currentOption?.link ? <Link to={currentOption.link}><FaArrowRight className="cursor-pointer" /></Link>
                    : (!currentOption && add) ? <a href={`${add}?next=close`} target="_blank" rel="noreferrer"><FaPlus className="cursor-pointer" /></a>
                        : null}
                <FaChevronDown className="cursor-pointer" />
            </div>
            {isFocused && <div className="absolute z-30 top-auto mt-1 left-0 right-0 shadow-lg rounded bg-white max-h-[250px] overflow-auto">
                {fullOptions.map(x => <div role="button" key={x.value} onClick={() => selectOption(x)} className={`w-full py-2 px-3 border-b last:border-b-0 border-gray-200 cursor-pointer hover:bg-blue-200 ${x.value === value ? 'bg-blue-200' : ''}`}>{x.label}</div>)}
                {!fullOptions.length && <div className="w-full p-5 text-center text-gray-600">{t('No data matching your search')}</div>}
            </div>}
        </div>
    </Label>
}

export function FileInput({ value, onChange, label, invalid, help, multiple, showImage, accept, className, ...props }) {
    const t = useTranslations()
    const [imgUrl, setImgUrl] = useState()
    const [inputProps, labelProps] = useErrors(value, invalid)
    const api = useApi()

    const isFile = value instanceof File
    const isMultipleFiles = multiple && value instanceof Array && value.some(x => x instanceof File)

    const remove = e => {
        e.preventDefault()
        onChange(null)
    }

    useEffect(() => {
        if (value && !isFile && !isMultipleFiles && showImage) {
            api.get(`/uploads/${encodeURIComponent(multiple ? value[0] : value)}`)
                .then(blob => setImgUrl(URL.createObjectURL(blob)))
                .catch(() => setImgUrl(multiple ? value[0] : value))
        } else setImgUrl(null)
    }, [value, multiple])

    return <Label label={label} className={className} help={help} disabled={props.disabled} {...labelProps}>
        <div className={`min-w-[272px] min-h-[42px] flex border border-gray-300 bg-gray-200 rounded-lg ${inputProps.className}`}>
            {!value || isFile || isMultipleFiles
                ? <label className="w-full px-3 py-2 cursor-pointer flex items-center gap-2">
                    {(isFile || isMultipleFiles) && <FaFile className="text-gray-600" />}
                    <div className="whitespace-pre">{isMultipleFiles ? value.map(x => x.name).join('\n') : isFile ? value.name : t('Choose a file...')}</div>
                    <input {...props} {...inputProps} key={label} onChange={e => onChange(multiple ? e.target.files.length ? [...e.target.files] : null : (e.target.files?.[0] || null))} type="file" accept={accept ? accept : showImage ? 'image/*' : undefined} multiple={multiple} className="hidden" />
                </label>
                : showImage
                    ? <div className="grow flex justify-center items-center">
                        {imgUrl ? <img src={imgUrl} crossOrigin="anonymous" alt={multiple ? value[0] : value} className="max-h-[200px]" /> : <Loading className="my-2" />}
                    </div>
                    : <div className="px-3 py-2 grow min-w-0 focus:outline-none focus:ring focus:ring-blue-300 focus:z-10 rounded-l-lg">
                        <div className="flex items-center">
                            <FaFile className="text-gray-600 mr-1" />
                            <a href={`${process.env.REACT_APP_API_URL}/uploads/${encodeURIComponent(multiple ? value[0] : value)}`} className="text-blue-500 underline cursor-pointer">
                                {decodeURIComponent(multiple ? value[0] : value).split('-').slice(1).join('-')}
                            </a>
                        </div>
                    </div>
            }
            {value && <div className="px-3 py-2 rounded-r-lg bg-gray-200 border-l border-gray-300 flex items-center text-gray-800 cursor-pointer" onClick={remove}>
                <FaTimes />
            </div>}
        </div>
    </Label>
}

export function ImageInput({ value, onChange, label, invalid, help, className, ...props }) {
    return <FileInput {...{ value, onChange, label, invalid, help, className, ...props, showImage: true }} />
}